import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { ThemeMode, DesignTokens } from '../shared/types/tokens';
import { systemColors, spacing, typography, radius } from '../shared/tokens';

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  tokens: DesignTokens;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Default to light mode first
  const [mode, setMode] = useState<ThemeMode>('light');

  useEffect(() => {
    // Check localStorage first, then use light mode as default (not system preference)
    if (typeof window === 'undefined') return;
    
    try {
      const saved = localStorage.getItem('theme-mode') as ThemeMode | null;
      // Only use saved preference, default to light mode (not system preference)
      if (saved === 'light' || saved === 'dark') {
        setMode(saved);
      }
      // Otherwise stay with light mode default
    } catch (error) {
      // localStorage might not be available, stay with light mode default
    }
  }, []);

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('theme-mode', newMode);
      } catch (error) {
        // Ignore localStorage errors
      }
    }
  };

  // Memoize tokens to prevent infinite re-renders
  const tokens: DesignTokens = useMemo(() => ({
    color: systemColors[mode] as DesignTokens['color'],
    spacing: spacing as DesignTokens['spacing'],
    typography: typography as DesignTokens['typography'],
    radius: radius as DesignTokens['radius'],
  }), [mode]);

  // Apply CSS variables (only on web/client)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    Object.entries(tokens.color).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    Object.entries(tokens.spacing).forEach(([key, value]) => {
      root.style.setProperty(`--spacing-${key}`, value);
    });
    root.style.setProperty('--font-family-body', tokens.typography.family.body.join(', '));
    root.style.setProperty('--font-family-heading', tokens.typography.family.heading.join(', '));
    Object.entries(tokens.typography.size).forEach(([key, value]) => {
      root.style.setProperty(`--font-size-${key}`, value);
    });
    Object.entries(tokens.typography.weight).forEach(([key, value]) => {
      root.style.setProperty(`--font-weight-${key}`, value);
    });
    Object.entries(tokens.radius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-${key}`, value);
    });
    root.setAttribute('data-theme', mode);
  }, [mode, tokens]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, toggleMode, tokens }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

